/**
 * ActionBadge — the ONE semantic palette for engine action labels
 * (C0 shared foundation, 2026-06-10).
 *
 * Fixes WU2 (high): action-badge colors contradicted across adjacent
 * surfaces — gold=BUY on the Compass home grid, blue=BUY on the brief it
 * links to, gold=TRIM on Anchor signals. Color is the pre-attentive cue in
 * a dense UI, so the mapping must be identical everywhere:
 *
 *   buy-side  (BUY / ADD / ACCUMULATE…)  → green  (bullish, cgreen #10B981)
 *   HOLD                                 → neutral (muted slate)
 *   WATCH                                → blue   (informational, not a trade)
 *   TRIM                                 → amber  (gold #F59E0B — reduce, not exit)
 *   SELL / EXIT / AVOID                  → red    (cred #EF4444)
 *   anything unrecognized                → neutral
 *
 * Server-component safe (no hooks, no "use client").
 *
 * REGULATORY NOTE (build-prompt Global rule 3): action labels must NEVER
 * render on logged-out/marketing surfaces. This component is for the
 * logged-in app (Compass / Anchor) only.
 *
 * Adoption (later waves): replace the per-page ACTION_CLASS / SIGNAL_CLASS
 * maps in compass/anchor pages with either
 *   <ActionBadge action={rec.action} />
 * or, where the surrounding markup must stay bespoke,
 *   <span className={cn("...", actionBadgeClass(rec.action))}>{rec.action}</span>
 */

import { cn } from "@/lib/utils/cn";

export type ActionSentiment = "buy" | "hold" | "watch" | "trim" | "sell" | "neutral";

/** Normalize an engine action string to its semantic bucket. */
export function actionSentiment(action: string | null | undefined): ActionSentiment {
  const a = (action ?? "").trim().toUpperCase().replace(/[\s_-]+/g, "_");
  if (a === "BUY" || a === "ADD" || a.startsWith("ACCUMULATE")) return "buy";
  if (a === "HOLD") return "hold";
  if (a === "WATCH" || a === "MONITOR") return "watch";
  if (a === "TRIM" || a === "REDUCE") return "trim";
  if (a === "SELL" || a === "EXIT" || a === "AVOID") return "sell";
  return "neutral";
}

const SENTIMENT_CLASS: Record<ActionSentiment, string> = {
  buy: "bg-cgreen/15 text-cgreen",
  hold: "bg-surface-light/30 text-muted-foreground",
  watch: "bg-blue/15 text-blue",
  trim: "bg-gold/15 text-gold",
  sell: "bg-red-500/15 text-red-400",
  neutral: "bg-surface-light/30 text-muted-foreground",
};

/**
 * Palette-only escape hatch: the bg/text classes for an action, for call
 * sites that need bespoke markup but the same colors.
 */
export function actionBadgeClass(action: string | null | undefined): string {
  return SENTIMENT_CLASS[actionSentiment(action)];
}

export interface ActionBadgeProps {
  /** Raw engine action label, e.g. "BUY", "ACCUMULATE", "TRIM". */
  action: string | null | undefined;
  /** Override the rendered text (defaults to the normalized label). */
  children?: React.ReactNode;
  /** sm = dense tables/rows (default), md = headers/panels. */
  size?: "sm" | "md";
  className?: string;
}

export function ActionBadge({ action, children, size = "sm", className }: ActionBadgeProps) {
  const label =
    children ?? (action == null ? "-" : action.trim().toUpperCase().replace(/[\s_-]+/g, " "));
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md font-semibold uppercase tracking-wide whitespace-nowrap",
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
        actionBadgeClass(action),
        className
      )}
    >
      {label}
    </span>
  );
}
